import { useRef } from 'react'
import type { HexItem } from '@/hooks/useHexItems'

/**
 * Hold the hex grid's *order* stable while `frozen` is true (a tile is expanded),
 * so a focused tile never gets yanked around by a re-sort mid-interaction.
 *
 * Data stays live: each id is re-looked-up against the latest `items`, so the
 * expanded tile and its neighbours keep updating their values — only their
 * positions are pinned. When `frozen` clears, the live order (already sorted
 * worst→best by the caller) takes over again.
 *
 * While frozen:
 *  - ids keep their previous slot,
 *  - ids that vanish are dropped,
 *  - genuinely new ids are appended at the end (so they appear without
 *    reshuffling the tiles the user is looking at).
 */
/**
 * Reorder `items` to honour a pinned id order: kept ids first (in `frozenIds`
 * order, with their latest data), then any new ids in `items` order. Pure — the
 * hook wraps this with the frozen-order ref. Exported for tests.
 */
export function applyFrozenOrder(items: HexItem[], frozenIds: string[]): HexItem[] {
  const byId = new Map(items.map((i) => [i.id, i]))
  const out: HexItem[] = []
  const seen = new Set<string>()

  for (const id of frozenIds) {
    const live = byId.get(id)
    if (live) {
      out.push(live)
      seen.add(id)
    }
  }
  for (const item of items) {
    if (!seen.has(item.id)) out.push(item)
  }
  return out
}

export function useStableHexOrder(items: HexItem[], frozen: boolean): HexItem[] {
  // Last order we committed while unfrozen — the layout to hold onto.
  const frozenOrderRef = useRef<string[]>([])

  if (!frozen) {
    frozenOrderRef.current = items.map((i) => i.id)
    return items
  }

  return applyFrozenOrder(items, frozenOrderRef.current)
}
